import React from 'react'
import { useMutation } from '@tanstack/react-query'
import { queryClient } from '@core/Data/ReactQuery/queryClient'
import { useOutsideClick } from 'rooks'
import { isReactNativeApp } from '@mobi/web-native-comms/web'
import { Button } from '@mobi/component-library/Common/V2/Buttons'
import { Icon } from '@mobi/component-library/Common/V2/Icon/Icon'
import { Tooltip } from '@mobi/component-library/Common/V2/Tooltip'
import { ContextMenu } from '@mobi/component-library/Overlays/ContextMenu'
import { triggerHapticFeedback } from '@core/Utils/hapticFeedback/hapticFeedback'
import { addToast, removeToast } from '@mobi/component-library/Feedback/Toast'
import { colors } from '@mobi/component-library/Theme/Common'
import { useAuthenticatedAccount } from '@core/Components/RequireAuth/hooks'
import { trackBetLoopNotificationToggle } from '@core/Areas/FollowMe/helpers/analytics'
import { handleApiErrorNotification } from '@core/Areas/FollowMe/helpers/handleApiErrorNotification'
import {
  ReportProfileButton,
  ReportProfileModal,
} from '@core/Areas/FollowMe/Components/Common/ReportProfile'
import { queryKeys } from '@core/Data/ReactQuery/constants'
import type { FollowButtonLocation } from './types'
import { UnfollowButtonComponent } from './Components/UnfollowButtonComponent'
import { NotificationButtonComponent } from './Components/NotificationButtonComponent'
import { FollowButtonComponent } from './Components/FollowButtonComponent'
import { checkPushNotificationRegistration } from './helpers/checkPushNotificationRegistration'
import { patchFeedDataWithNotificationChange } from './helpers/patchReactQueryData'
import { useFollowMutation } from './hooks/useFollowMutation'

type Props = {
  accountId: string
  accountDisplayName: string
  isFollowing: boolean
  isNotificationsEnabled: boolean
  location: FollowButtonLocation
  isOnProfilePage?: boolean
}

export const FollowButton: React.FC<Props> = ({
  accountId,
  accountDisplayName,
  isFollowing,
  isNotificationsEnabled,
  location,
  isOnProfilePage = false,
}) => {
  const notificationButtonRef = React.useRef<HTMLButtonElement>(null)
  const sharedBetCardFollowElRef = React.useRef<HTMLDivElement>(null)
  const contextMenuTriggerElRef = React.useRef<HTMLButtonElement>(null)

  const shouldEnableNotifications = isReactNativeApp || process.env.NODE_ENV === 'development'

  const [isFollowingLocalState, setIsFollowingLocalState] = React.useState(isFollowing)
  const [isContextMenuOpen, setIsContextMenuOpen] = React.useState(false)
  const [shouldShowAlertsTooltip, setShouldShowAlertsTooltip] = React.useState(false)
  const [isReportProfileModalOpen, setIsReportProfileModalOpen] = React.useState(false)
  const [hasEnabledNotifications, setHasEnabledNotifications] =
    React.useState(isNotificationsEnabled)

  React.useEffect(() => setIsFollowingLocalState(isFollowing), [isFollowing])
  React.useEffect(
    () => setHasEnabledNotifications(isNotificationsEnabled),
    [isNotificationsEnabled]
  )

  const { accountNumber } = useAuthenticatedAccount()

  useOutsideClick(sharedBetCardFollowElRef, () => {
    if (shouldShowAlertsTooltip) setShouldShowAlertsTooltip(false)
    if (isContextMenuOpen) setIsContextMenuOpen(false)
  })

  const onFollowSuccess = (newIsFollowingState: boolean) => {
    setIsFollowingLocalState(newIsFollowingState)
    if (newIsFollowingState) {
      setShouldShowAlertsTooltip(shouldEnableNotifications)
      displayFollowSuccessToast(accountDisplayName)
    }
  }

  const followMutation = useFollowMutation({
    profileId: accountId,
    isFollowingLocalState,
    onFollowSuccess,
  })

  const notifyMutation = useMutation({
    mutationFn: () =>
      checkPushNotificationRegistration(accountId, hasEnabledNotifications, accountNumber),
    onSuccess: () => {
      const newNotifState = !hasEnabledNotifications
      setHasEnabledNotifications(newNotifState)
      const enabledToastId = 'notification-success-' + accountId
      if (newNotifState) {
        addToast({
          id: enabledToastId,
          title: `Notifications enabled for ${accountDisplayName}`,
          icon: 'success',
          theme: 'dark',
          isDismissable: true,
        })
      } else {
        removeToast({ id: enabledToastId })
      }
      triggerHapticFeedback('impact-light')
      trackBetLoopNotificationToggle(!hasEnabledNotifications ? 'enable' : 'disable')
      patchFeedDataWithNotificationChange({ accountId, isNotificationsEnabled: newNotifState })
      queryClient.invalidateQueries({ queryKey: queryKeys.followMeAccountProfileBase })
    },
    onError: e => handleApiErrorNotification(e),
  })

  React.useEffect(() => {
    if (!isOnProfilePage || !isContextMenuOpen || !sharedBetCardFollowElRef.current) return
    const observer = new IntersectionObserver(
      ([entry]: IntersectionObserverEntry[]) => {
        if (!entry.isIntersecting) setIsContextMenuOpen(false)
      },
      { rootMargin: `-${200}px 0px 0px 0px`, threshold: 0.1 }
    )
    observer.observe(sharedBetCardFollowElRef.current)
    return () => observer.disconnect()
  }, [isOnProfilePage, isContextMenuOpen])

  const handleFollowClick = () => followMutation.mutate()
  const handleNotifyClick = () => notifyMutation.mutate()

  if (!isFollowingLocalState) {
    return (
      <FollowButtonComponent
        isPending={followMutation.isPending}
        handleFollowClick={handleFollowClick}
        location={location}
      />
    )
  }

  return location === 'shared-bet-card' ? (
    <>
      <div ref={sharedBetCardFollowElRef}>
        <Button
          ref={contextMenuTriggerElRef}
          color='secondary_color'
          size='sm'
          data-testid='shared-bet-context-menu-btn'
          onClick={() => setIsContextMenuOpen(curr => !curr)}
          style={{ maxWidth: '3.6rem' }}
        >
          <Icon name='DuocolorDotsHorizontal' color={colors.studio[600]} size='2rem' />
        </Button>

        {shouldShowAlertsTooltip && !hasEnabledNotifications && (
          <Tooltip
            color='blue'
            isHidden={notifyMutation.isPending}
            shouldHideCrossIcon
            triggerElRef={sharedBetCardFollowElRef}
            hasTooltipBeenSeen={() => false}
            tooltipId='bet-loop-turn-alerts-on'
            onDismissed={() => setShouldShowAlertsTooltip(false)}
            shouldOnlyRenderSingleInstance={true}
            offsetYPx={13}
            offsetXPx={-35}
            flipElement={true}
          >
            <div>Get notified for</div>
            <div>every new bet</div>
          </Tooltip>
        )}

        <ContextMenu
          isOpen={isContextMenuOpen}
          referenceElRef={contextMenuTriggerElRef}
          offsetYPx={5}
          placement='bottom-end'
          onDismiss={() => setIsContextMenuOpen(false)}
        >
          {shouldEnableNotifications ? (
            <Button
              ref={notificationButtonRef}
              size='sm'
              data-testid={`bet-loop-${hasEnabledNotifications ? 'disable' : 'enable'}-notifications`}
              color='secondary_outline'
              disabled={notifyMutation.isPending}
              onClick={() => {
                handleNotifyClick()
                setIsContextMenuOpen(false)
              }}
            >
              <div>
                {hasEnabledNotifications ? (
                  <Icon color={colors.studio[500]} name='SolidBellRinging01' />
                ) : (
                  <Icon name='LineBellPlus' />
                )}
                <span>Notifications</span>
              </div>
            </Button>
          ) : null}

          <Button
            size='sm'
            color='secondary_outline'
            disabled={followMutation.isPending}
            onClick={() => {
              handleFollowClick()
              setIsContextMenuOpen(false)
            }}
          >
            <div style={{ minWidth: '11rem' }}>
              <Icon name='DuocolorUsersX' />
              <span>Unfollow</span>
            </div>
          </Button>

          <ReportProfileButton onClick={() => setIsReportProfileModalOpen(true)} />
        </ContextMenu>
      </div>

      <ReportProfileModal
        isOpen={isReportProfileModalOpen}
        onDismiss={() => setIsReportProfileModalOpen(false)}
        accountId={accountId}
      />
    </>
  ) : (
    <>
      <UnfollowButtonComponent
        isPending={followMutation.isPending}
        handleFollowClick={handleFollowClick}
        location={location}
      />

      <NotificationButtonComponent
        isPending={notifyMutation.isPending}
        handleNotifyClick={handleNotifyClick}
        isNotificationsEnabledState={hasEnabledNotifications}
        shouldShowAlertsTooltip={shouldShowAlertsTooltip}
        setShouldShowAlertsTooltip={setShouldShowAlertsTooltip}
        location={location}
      />
    </>
  )
}

// =============
// Local Helpers
// =============

function displayFollowSuccessToast(profileName: string) {
  addToast({
    id: 'bet-loop-follow-success',
    isDismissable: true,
    title: `You started following ${profileName}!`,
    icon: 'success',
    theme: 'dark',
  })
}
